import Backbone from '@polleverywhere/backbone'
import ProxyPolyfill from 'proxy-polyfill/src/proxy'

window.Proxy = window.Proxy || ProxyPolyfill()

function standaloneProxy (sourceModel) {
  // create a proxy with the same class
  const model = sourceModel.clone()

  // make proxy
  proxyModel(model)

  // set source so get/set handler know what the real source is
  model.__proxy_source__ = sourceModel.__proxy_source__ || sourceModel

  // propagate changes from source model
  model.listenTo(sourceModel, 'change', () => {
    model.set(sourceModel.changedAttributes())
  })

  // propagate events to source
  model.on('all', (event, proxyModel, ...args) => {
    sourceModel.trigger(event, model, ...args)
  })

  return model
}

export function proxyModel (model, options = {}) {
  options = Object.assign({
    standalone: false
  }, options)

  if (options.standalone) {
    return standaloneProxy(model)
  }

  if (model.__proxy__) return model

  model.__proxy__ = new Backbone.Model()

  trapProxyMethods(model)

  model.listenTo(model.__proxy__, 'all', (event, proxyModel, ...args) => {
    model.trigger(event, model, ...args)
  })

  return model
}

function trapProxyMethods (model) {
  model.proxy = proxy.bind(model)
  model.deproxy = deproxy.bind(model)
  model.getProxiedAttrs = getProxiedAttrs.bind(model)
  model.get = new Proxy(model.get, { apply: getHandler })
  model.save = new Proxy(model.save, { apply: saveHandler })
}

function formatArgsForBackboneSet (attribute, value, options = {}) {
  let attrs = {}

  if (typeof attribute === 'object') {
    if (attribute) {
      attrs = attribute
    }

    options = value || options
  } else {
    attrs[attribute] = value
  }

  return { attrs, options }
}

function getProxiedAttrs () {
  if (this.__proxy__) {
    return Object.keys(this.__proxy__.attributes || {})
  } else {
    return []
  }
}

function proxy () {
  const { attrs, options } = formatArgsForBackboneSet.apply(null, arguments)

  if (!this.__proxy__) {
    proxyModel(this)
  }

  return this.__proxy__.set(attrs, options)
}

function deproxy (attr, options = {}) {
  if (this.__proxy__) {
    if (attr) {
      const silent = this.__proxy__.attributes[attr] === this.attributes[attr]
      this.__proxy__.unset(attr, Object.assign({}, options, { silent }))
    } else {
      Object.keys(this.__proxy__.attributes).forEach((attribute) => {
        const silent = this.__proxy__.attributes[attribute] === this.attributes[attribute]
        this.trigger(`change:${attribute}`, this, this.attributes[attribute], Object.assign({}, options, { silent }))
      })
      this.trigger('change', this, options)

      // Passing in silent here because we're triggering the individual change events above.
      this.__proxy__.clear({ silent: true })
    }
  }

  return this
}

function getHandler (target, thisArg, argumentsList) {
  const proxied = argumentsList && argumentsList.length && thisArg && thisArg.__proxy__

  if (!!proxied && proxied.attributes[argumentsList[0]] !== undefined) {
    return thisArg.__proxy__.attributes[argumentsList[0]]
  } else {
    return target.apply(thisArg, argumentsList)
  }
}

function saveHandler (target, thisArg, argumentsList) {
  const { attrs, options } = formatArgsForBackboneSet.apply(null, argumentsList)

  if (options.proxy && thisArg.__proxy__ && thisArg.__proxy__.attributes) {
    Object.assign(attrs, thisArg.__proxy__.attributes)
  }

  return target.call(thisArg, attrs, options)
}
